import React from 'react';

const Ethics = () => {
    return (
        <div style={styles.page}>
            <h1 style={styles.header}>Publication Ethics</h1>
            <p style={styles.intro}>
                <em>Innovative Trends in Multidisciplinary Engineering (ITME)</em> is committed to upholding the highest standards of integrity, transparency, and ethical responsibility in scholarly publishing. The journal strictly adheres to internationally recognized principles of publication ethics and takes all necessary measures to prevent academic misconduct. Ethical compliance is considered a fundamental requirement for authors, reviewers, editors, and all stakeholders involved in the publication process. Any breach of ethical standards will be addressed with appropriate corrective action to preserve the credibility and integrity of the journal.
            </p>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>Duties and Responsibilities of Authors</h2>
                <p>
                    Authors submitting manuscripts to ITME are required to ensure that their work is original, has not been previously published, and is not under consideration for publication elsewhere. Proper acknowledgment and citation of all sources, data, and prior research must be provided. Plagiarism in any form, including self-plagiarism, duplicate submission, or improper paraphrasing, is strictly prohibited.
                </p>
                <p>
                    Authors must not engage in data fabrication, falsification, manipulation of images, or misrepresentation of research findings. All listed authors must have made significant academic contributions to the work, and inappropriate authorship practices such as ghost authorship or honorary authorship are unacceptable. Where research involves human participants, animals, or sensitive data, authors must provide confirmation of ethical approval from the appropriate institutional review board or ethics committee.
                </p>
                <p>
                    Authors are also required to disclose any financial, institutional, or personal conflicts of interest that could influence the results or interpretation of their research. Simultaneous submission of the same manuscript to multiple journals is strictly prohibited and constitutes unethical behaviour.
                </p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>Duties and Responsibilities of Editors</h2>
                <p>
                    Editors of ITME are responsible for evaluating submitted manuscripts solely on the basis of their academic merit, originality, relevance to the journal's scope, and scientific rigor. Editorial decisions must be free from discrimination based on authors' gender, ethnicity, institutional affiliation, nationality, or personal beliefs.
                </p>
                <p>
                    Editors are required to maintain confidentiality regarding all submitted manuscripts and related communications. They must avoid conflicts of interest and recuse themselves from handling manuscripts where such conflicts exist. Editorial decisions should be based on reviewer evaluations, journal policies, and scholarly standards. In cases of suspected ethical misconduct, editors are obligated to initiate appropriate investigations and take corrective actions in accordance with established academic publishing practices.
                </p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>Duties and Responsibilities of Reviewers</h2>
                <p>
                    Reviewers play a critical role in maintaining the academic quality of the journal. They are expected to provide objective, constructive, and timely evaluations of manuscripts. Reviewers must treat all submitted manuscripts as confidential documents and must not disclose or use any unpublished information for personal advantage.
                </p>
                <p>
                    Reviewers should identify relevant published work that has not been cited and alert the editorial team to any substantial similarity or overlap with previously published material. Personal criticism of authors is inappropriate; feedback must remain professional and academically focused. If a reviewer identifies a conflict of interest or feels unqualified to review a manuscript, they must promptly inform the editorial office.
                </p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>Plagiarism and Academic Misconduct Policy</h2>
                <p>
                    All submitted manuscripts undergo plagiarism screening using recognized similarity detection software. Manuscripts exceeding acceptable similarity thresholds may be returned to authors for clarification or revision or may be rejected at the discretion of the editorial board. Intentional plagiarism, data manipulation, or academic fraud may result in immediate rejection, withdrawal of published articles, and possible blacklisting of the authors from future submissions.
                </p>
                <p>
                    The journal maintains a zero-tolerance policy toward academic misconduct and reserves the right to notify affiliated institutions in cases of severe ethical violations.
                </p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>Conflict of Interest Disclosure</h2>
                <p>
                    All participants in the publication process, including authors, reviewers, and editors, must disclose any financial, institutional, or personal relationships that could influence the evaluation or interpretation of research findings. Transparency in disclosure ensures the integrity and impartiality of the editorial process.
                </p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>Retraction, Correction, and Expression of Concern</h2>
                <p>
                    ITME reserves the right to publish corrections, clarifications, expressions of concern, or retractions when necessary. Articles may be retracted in cases involving proven ethical misconduct, serious methodological errors, plagiarism, or unreliable findings. Retraction notices will clearly state the reasons for retraction to maintain transparency and uphold academic integrity.
                </p>
                <p>
                    Minor errors that do not compromise the scientific validity of the work may be corrected through formal corrigenda or errata published by the journal.
                </p>
            </section>

            <section style={styles.section}>
                <h2 style={styles.sectionHeader}>Data Transparency and Reproducibility</h2>
                <p>
                    Authors are encouraged to maintain transparency regarding research data and methodologies to support reproducibility. Where applicable, datasets, supplementary materials, and analytical procedures should be made available upon reasonable request. The journal supports open scientific practices that enhance verifiability and research reliability.
                </p>
            </section>

            <div style={styles.downloadWrapper}>
                <a href="/Ethics.docx" download style={styles.downloadBtn}>
                    <span>Download Ethics Document</span>
                    <svg style={styles.downloadIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                </a>
            </div>
        </div>
    );
};

const styles = {
    page: {
        maxWidth: '800px',
        margin: 'auto',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        lineHeight: '1.6',
        color: '#333',
        textAlign: 'justify',
    },
    header: {
        fontSize: '2.2rem',
        background: 'linear-gradient(90deg, #004aad, #0078d7)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textAlign: 'center',
        marginBottom: '20px',
    },
    intro: {
        fontSize: '1.1rem',
        marginBottom: '20px',
        textAlign: 'justify',
    },
    section: {
        marginBottom: '20px',
        borderBottom: '2px solid #e0e0e0',
        paddingBottom: '15px',
    },
    sectionHeader: {
        fontSize: '1.5rem',
        color: '#0078d7',
    },
    downloadWrapper: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '30px',
        marginBottom: '30px',
    },
    downloadBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        padding: '14px 32px',
        background: 'linear-gradient(135deg, #004aad, #0078d7)',
        color: '#fff',
        fontSize: '1.05rem',
        fontWeight: '600',
        borderRadius: '8px',
        textDecoration: 'none',
        transition: 'transform 0.2s, box-shadow 0.2s',
        boxShadow: '0 4px 14px rgba(0, 74, 173, 0.25)',
        cursor: 'pointer',
    },
    downloadIcon: {
        width: '20px',
        height: '20px',
    },
};

export default Ethics;
